import { z } from 'zod';

/**
 * `pending-revenue-weighted-coverage` v0–v1 — wire types for the
 * dollar-weighted coverage view that surfaces in release-readiness,
 * PR comments, and the dashboard tile.
 *
 * Locked decisions (2026-05-28 + v1 amendment 2026-05-31):
 *   - Manual tagging only in v0; analytics auto-tagging is v0.2.
 *   - NULL tier = excluded from weight math.
 *   - Default weights `high: 1.0, med: 0.5, low: 0.1` —
 *     operator-tunable via `coverage.revenueTierWeights`.
 *   - **v1 currency layer (2026-05-31).** Operator-tunable
 *     `dollarValuePerTier` (default `0` per tier — opt-in by
 *     tuning) translates the weighted percent into a
 *     `$covered of $total` headline. Currency is a 3-letter ISO
 *     4217 code (default `'USD'`); the API never converts between
 *     currencies — the operator picks one and every tier is
 *     denominated in that currency.
 */

export const REVENUE_TIERS = ['high', 'med', 'low'] as const;
export type RevenueTier = (typeof REVENUE_TIERS)[number];

export const RevenueTierWeightsSchema = z.object({
  high: z.number().min(0).max(1).default(1.0),
  med: z.number().min(0).max(1).default(0.5),
  low: z.number().min(0).max(1).default(0.1),
});
export type RevenueTierWeights = z.infer<typeof RevenueTierWeightsSchema>;

/**
 * v1 — per-tier dollar value. `0` (the default) means the operator
 * has not opted in to the currency layer; the tile + PR line render
 * the percent-only v0.1 shape. Any non-zero tier flips the renderer
 * to the dollar shape.
 *
 * Bounded `[0, 1_000_000_000]` (1 billion per tier) so an obviously
 * wrong value (e.g. `1e18`) is caught at the contracts layer instead
 * of overflowing the UI.
 */
export const RevenueDollarValuePerTierSchema = z.object({
  high: z.number().min(0).max(1_000_000_000).default(0),
  med: z.number().min(0).max(1_000_000_000).default(0),
  low: z.number().min(0).max(1_000_000_000).default(0),
});
export type RevenueDollarValuePerTier = z.infer<typeof RevenueDollarValuePerTierSchema>;

/**
 * v1 — ISO 4217 3-letter currency code. Validated as 3 uppercase
 * letters at the contracts layer; downstream renderers feed this to
 * `Intl.NumberFormat(..., { style: 'currency', currency })` which
 * gracefully handles any code the runtime knows.
 */
export const RevenueCurrencyCodeSchema = z
  .string()
  .regex(/^[A-Z]{3}$/, 'Currency must be a 3-letter ISO 4217 code (e.g. "USD", "EUR", "INR").')
  .default('USD');
export type RevenueCurrencyCode = z.infer<typeof RevenueCurrencyCodeSchema>;

/**
 * v1 — dollar summary attached to `RevenueWeightedCoverage` when
 * the operator has configured non-zero `dollarValuePerTier`. All
 * three numbers are denominated in the configured currency; the API
 * never round-trips them through a conversion.
 */
export const RevenueWeightedDollarSummarySchema = z.object({
  /** Total dollars across every tagged flow's tier value. */
  totalDollars: z.number().min(0),
  /** Dollars across tagged flows that PASSED in this run scope. */
  coveredDollars: z.number().min(0),
  /** ISO 4217 3-letter code (operator-tunable, default `'USD'`). */
  currency: RevenueCurrencyCodeSchema,
});
export type RevenueWeightedDollarSummary = z.infer<typeof RevenueWeightedDollarSummarySchema>;

export const RevenueWeightedCoverageSchema = z.object({
  /** Total weighted score across all tagged flows in scope. */
  totalWeight: z.number().min(0),
  /** Weighted score across the tagged flows that PASSED in this run scope. */
  coveredWeight: z.number().min(0),
  /** `coveredWeight / totalWeight` (0..1). Renders as a percent in the UI. */
  weightedPercent: z.number().min(0).max(1),
  /** Per-tier flow counts so the UI can render "high: 12/14, med: 30/40". */
  perTier: z.record(
    z.enum(REVENUE_TIERS),
    z.object({ covered: z.number().int().nonnegative(), total: z.number().int().nonnegative() }),
  ),
  /**
   * Number of flows in scope that had `revenue_tier IS NULL` —
   * surfaced verbatim so the operator knows the score's denominator
   * is incomplete (locked decision §3).
   */
  untaggedFlows: z.number().int().nonnegative(),
  /**
   * v1 — dollar summary. Present iff the operator has opted in by
   * setting at least one `coverage.dollarValuePerTier` value above
   * `0`. Absent when the operator stays on the percent-only v0.1
   * shape; renderers fall back to the existing percent path.
   */
  dollars: RevenueWeightedDollarSummarySchema.optional(),
});
export type RevenueWeightedCoverage = z.infer<typeof RevenueWeightedCoverageSchema>;
